interface SecurityConfig {
  enabled: boolean;
  features: {
    preventDevTools: boolean;
    preventCopy: boolean;
    preventRightClick: boolean;
    preventKeyboardShortcuts: boolean;
    encryptData: boolean;
    disableConsole: boolean;
  };
  whitelistedPaths: string[];
  enforceAuthForEnterprise: boolean;
}

// Helper to safely access environment variables with proper type checking
const getEnvVar = (key: string, defaultValue: string | boolean): string | boolean => {
  const envKey = `VITE_${key}`;
  const value = import.meta.env[envKey];

  if (typeof defaultValue === 'boolean') {
    // Explicitly check for string 'true' or 'false'
    if (value === 'true') return true;
    if (value === 'false') return false;
    return defaultValue;
  }
  return value || defaultValue;
};

// Default configuration - all security features disabled by default
const defaultConfig: SecurityConfig = {
  enabled: import.meta.env.PROD, // Only enabled in production by default
  features: {
    preventDevTools: false,
    preventCopy: false,
    preventRightClick: false,
    preventKeyboardShortcuts: false,
    encryptData: true, // Only encryption enabled by default
    disableConsole: false,
  },
  whitelistedPaths: ['/docs', '/api-documentation'],
  enforceAuthForEnterprise: import.meta.env.PROD, // Default to true in production, false otherwise
};

// Create the security configuration from environment variables
export let securityConfig: SecurityConfig = {
  enabled: getEnvVar('SECURITY_ENABLED', defaultConfig.enabled) as boolean,
  features: {
    preventDevTools: getEnvVar('PREVENT_DEVTOOLS', defaultConfig.features.preventDevTools) as boolean,
    preventCopy: getEnvVar('PREVENT_COPY', defaultConfig.features.preventCopy) as boolean,
    preventRightClick: getEnvVar('PREVENT_RIGHT_CLICK', defaultConfig.features.preventRightClick) as boolean,
    preventKeyboardShortcuts: getEnvVar('PREVENT_KEYBOARD_SHORTCUTS', defaultConfig.features.preventKeyboardShortcuts) as boolean,
    encryptData: getEnvVar('ENCRYPT_DATA', defaultConfig.features.encryptData) as boolean,
    disableConsole: getEnvVar('DISABLE_CONSOLE', defaultConfig.features.disableConsole) as boolean,
  },
  whitelistedPaths: (getEnvVar('SECURITY_WHITELIST', '') as string)
    .split(',')
    .filter(Boolean) || defaultConfig.whitelistedPaths,
  enforceAuthForEnterprise: getEnvVar('ENFORCE_AUTH_FOR_ENTERPRISE', defaultConfig.enforceAuthForEnterprise) as boolean,
};

/**
 * Fetches the global security config from the backend and mutates the singleton.
 * Safe to run on boot; if it fails, falls back perfectly to the default env-based config.
 */
export const fetchAndApplySecurityConfig = async (): Promise<void> => {
  try {
    const { getSecurityConfigClient } = await import('@/api/grpc/securityConfigClient');
    const securityConfigClient = getSecurityConfigClient();
    const response = await securityConfigClient.getSecurityConfig({});
    if (response.config) {
      const cfg = response.config;
      securityConfig.enabled = cfg.enabled;
      securityConfig.features.preventDevTools = cfg.preventDevTools;
      securityConfig.features.preventCopy = cfg.preventCopy;
      securityConfig.features.preventRightClick = cfg.preventRightClick;
      securityConfig.features.preventKeyboardShortcuts = cfg.preventKeyboardShortcuts;
      securityConfig.features.disableConsole = cfg.disableConsole;
      securityConfig.features.encryptData = cfg.encryptData;
      securityConfig.whitelistedPaths = cfg.whitelistedPaths;
    }
  } catch {
    console.warn('Failed to fetch security configurations from backend. Using environment defaults.');
  }
};


// Helper to check if security should be enabled for a specific path
export const isSecurityEnabledForPath = (path: string): boolean => {
  // If security is not enabled globally, return false
  if (!securityConfig.enabled) {
    return false;
  }

  // Check if path is whitelisted
  const isWhitelisted = securityConfig.whitelistedPaths.some(whitePath =>
    path === whitePath || path.startsWith(`${whitePath}/`)
  );

  return !isWhitelisted;
};
